/**
 * /open-app#<app address><path>
 *
 * Someone reached one of a student's app addresses
 * (<port>-<environment id>.preview.<domain>) without its sign-in: a bookmark, a
 * shared link, a sign-in that expired. The pod's nginx sends page loads here
 * (images/_shared/scripts/patch-nginx-app-preview.sh). If the signed-in viewer
 * may see that app -- the student, or staff who manage the lab -- the backend
 * returns a one-hop sign-in link and this page goes there, to the path they
 * asked for. Otherwise it says why.
 *
 * The address travels in the fragment: never sent to a server, and the app's
 * own path and query need no second encoding.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { studentPortal } from '../services/api';

const APP_HOST = /^[1-9][0-9]{3,4}-[a-f0-9]{24}\.preview\.[a-z0-9.-]+$/;

/** { host, path } from the fragment, or null when it is not an app address. */
export function parseAppAddress(hash) {
  const raw = String(hash || '').replace(/^#/, '');
  const slash = raw.indexOf('/');
  const host = (slash === -1 ? raw : raw.slice(0, slash)).toLowerCase();
  const path = slash === -1 ? '/' : raw.slice(slash);
  return APP_HOST.test(host) ? { host, path } : null;
}

export default function OpenApp({ go = (url) => window.location.replace(url) }) {
  const [error, setError] = useState(null);

  useEffect(() => {
    const target = parseAppAddress(window.location.hash);
    if (!target) {
      setError('This link does not point to an app.');
      return undefined;
    }
    let cancelled = false;
    studentPortal.appLink(target.host, target.path)
      .then(({ url }) => {
        if (cancelled) return;
        if (typeof url !== 'string' || !url.startsWith('https://')) throw new Error('not a sign-in link');
        go(url);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'The app could not be opened. Try again from the lab environment.');
        }
      });
    return () => { cancelled = true; };
  }, [go]);

  return (
    <div style={{ maxWidth: 520, margin: '15vh auto', padding: 24, textAlign: 'center' }}>
      {error ? (
        <div role="alert">
          <h1 style={{ fontSize: 20, marginBottom: 12 }}>The app could not be opened</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>{error}</p>
          <Link to="/" className="btn btn-secondary">Go to your dashboard</Link>
        </div>
      ) : (
        <div role="status" aria-live="polite">
          <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto 16px' }} aria-hidden="true" />
          <p>Opening the app…</p>
        </div>
      )}
    </div>
  );
}
